import { Fragment, useMemo } from 'react';
import Image from 'next/image';

// components
import { Icon } from '@/components/ui/icon';
import Link from '@/components/utilities/SmartLink';

// hooks
import { useAppContext } from '@/components/utilities/AppContext';

// utilities
import VisibleLoading from '@/components/utilities/VisibleLoading';
import YoutubeVideo from '@/components/utilities/YoutubeVideo';
import { getImagePath } from '@/utils/images';

const TutorialsBlock = ({ categories }) => {
  const { rootPath, restOfPath } = useAppContext();

  const [selectedCategory, selectedItem] = useMemo(() => {
    const parts = (restOfPath || '').split('/').filter(Boolean);

    const categoryId = parts[0] || null;
    const itemId = parts[1] || null;

    const category = categories.find((cat) => cat.categoryId === categoryId) || null;

    if (category) {
      const item = category.items.find((it) => it.itemId === itemId) || null;

      return [category, item];
    }

    return [null, null];
  }, [restOfPath, categories]);

  return selectedItem ? (
    <div className="mx-auto w-[90vw] xl:w-(--container-width)">
      <div>
        <VisibleLoading delay={100}>
          {({ ref, className }) => (
            <h3 ref={ref} className={`mb-8 text-center ${className || ''}`.trim()}>
              {selectedItem.title}
            </h3>
          )}
        </VisibleLoading>

        <VisibleLoading delay={150}>
          {({ ref, className }) => (
            <p
              ref={ref}
              className={`mx-auto mb-8 max-w-[600px] text-center whitespace-pre-wrap ${className || ''}`.trim()}
            >
              {selectedItem.description}
            </p>
          )}
        </VisibleLoading>

        <VisibleLoading>
          {({ ref, className }) => (
            <div
              ref={ref}
              className={`border-white-75 relative z-2 mx-auto h-[200px] w-full min-w-[200px] rounded-3xl border border-8 object-cover xl:h-[640px] ${className || ''}`.trim()}
            >
              <YoutubeVideo
                videoId={selectedItem.videoId}
                thumbnail={
                  selectedItem.image ? (
                    <Image
                      src={getImagePath(selectedItem.image)}
                      alt={selectedItem.image.alternativeText || selectedItem.title}
                      fill={true}
                      className="object-cover"
                    />
                  ) : null
                }
              />
            </div>
          )}
        </VisibleLoading>
      </div>
    </div>
  ) : (
    <VisibleLoading delay={100}>
      {({ ref, className }) => (
        <>
          <div ref={ref} />
          <div
            className={`mx-auto my-5 flex w-[90vw] flex-col gap-6 rounded-3xl bg-white/50 p-6 backdrop-blur-2xl xl:my-10 xl:w-(--container-width) xl:gap-10 xl:p-20 ${className || ''}`.trim()}
          >
            {categories.map((category, index) => (
              <Fragment key={category.id}>
                <Category category={category} rootPath={rootPath} />
                {index < categories.length - 1 && <hr className="border-black/20" />}
              </Fragment>
            ))}
          </div>
        </>
      )}
    </VisibleLoading>
  );
};
export default TutorialsBlock;

const Category = ({ rootPath, category }) => {
  return (
    <div className="flex flex-col gap-6 xl:flex-row">
      <div className="flex-1">
        <h5>{category.title}</h5>
      </div>
      <div className="flex flex-1 flex-col gap-2 xl:gap-4">
        {category.items.map((item) => (
          <Item key={item.id} rootPath={rootPath} categoryId={category.categoryId} item={item} />
        ))}
      </div>
    </div>
  );
};

const Item = ({ rootPath, categoryId, item }) => {
  const link = `${rootPath}/${categoryId}/${item.itemId}`;

  return (
    <div>
      <Link href={link} className="group">
        <div className="group-hover:bg-brand-100 bg-brand-50 inline-flex items-center gap-3 rounded-full p-1.5 pr-2 transition-colors">
          <div className="bg-brand-400 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white shadow-[0_10.276px_17.617px_3.67px_rgba(254,64,17,0.15),2.202px_2.936px_8.074px_0_rgba(254,64,17,0.20)]">
            <Icon type="play" width={17} height={17} />
          </div>
          <div className="txt-lg font-medium">{item.title}</div>
        </div>
      </Link>
    </div>
  );
};
